/**
 * Studio video-source pointer (cue-forge-9yc.9). Persists a *reference* to the
 * loaded video locally so a reload can re-open it — NOT the bytes. When the
 * browser supports the File System Access API we keep a re-openable
 * FileSystemFileHandle (restored from disk after a permission prompt);
 * otherwise just the filename (for a re-attach prompt). Local only, never
 * uploaded. Stored in its own IndexedDB so it doesn't touch the autosave DB.
 */

const DB_NAME = 'cueforge-studio';
const STORE = 'video';
const KEY = 'current';
const DB_VERSION = 1;

export interface VideoRef {
  name: string;
  /** Re-openable handle when the File System Access API is available. */
  handle?: FileSystemFileHandle;
}

/** A FileSystemFileHandle plus the (non-standard-typed) permission methods. */
interface PermissionedHandle {
  queryPermission?(desc: { mode: 'read' }): Promise<PermissionState>;
  requestPermission?(desc: { mode: 'read' }): Promise<PermissionState>;
  getFile(): Promise<File>;
}

interface FilePickerWindow {
  showOpenFilePicker?: (opts?: {
    multiple?: boolean;
    types?: { description?: string; accept: Record<string, string[]> }[];
  }) => Promise<FileSystemFileHandle[]>;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

function toPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

async function inStore<T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  try {
    return await toPromise(op(db.transaction(STORE, mode).objectStore(STORE)));
  } finally {
    db.close();
  }
}

/** Persist the video reference (overwrites the single slot). */
export async function saveVideoRef(ref: VideoRef): Promise<void> {
  await inStore('readwrite', (s) => s.put(ref, KEY));
}

/** Read the saved video reference, or null if there is none. */
export async function loadVideoRef(): Promise<VideoRef | null> {
  const ref = await inStore<VideoRef | undefined>(
    'readonly',
    (s) => s.get(KEY) as IDBRequest<VideoRef | undefined>,
  );
  return ref ?? null;
}

/** Forget the saved video reference. */
export async function clearVideoRef(): Promise<void> {
  await inStore('readwrite', (s) => s.delete(KEY));
}

/** True when the browser can re-open a file from a persisted handle. */
export function supportsFileHandles(): boolean {
  return typeof window !== 'undefined' && 'showOpenFilePicker' in window;
}

/**
 * Open a video via the File System Access picker, returning the file and its
 * re-openable handle. Null if unsupported or the user cancelled.
 */
export async function pickVideoWithHandle(): Promise<{
  file: File;
  handle: FileSystemFileHandle;
} | null> {
  const picker = (window as unknown as FilePickerWindow).showOpenFilePicker;
  if (!picker) return null;
  try {
    const [handle] = await picker({
      multiple: false,
      types: [
        {
          description: 'Video',
          accept: { 'video/*': ['.mp4', '.webm', '.m4v', '.mov', '.mkv', '.ogg', '.ogv'] },
        },
      ],
    });
    if (!handle) return null;
    const file = await handle.getFile();
    return { file, handle };
  } catch {
    return null; // user cancelled the picker
  }
}

/**
 * Re-open a file from a persisted handle, prompting for read permission if
 * needed. Null when permission is denied or the file is gone/moved.
 */
export async function reopenFromHandle(handle: FileSystemFileHandle): Promise<File | null> {
  const h = handle as unknown as PermissionedHandle;
  try {
    const desc = { mode: 'read' } as const;
    const existing = (await h.queryPermission?.(desc)) ?? 'prompt';
    const granted = existing === 'granted' ? 'granted' : await h.requestPermission?.(desc);
    if (granted !== 'granted') return null;
    return await h.getFile();
  } catch {
    return null;
  }
}
